import React, { useEffect, useImperativeHandle, useRef } from 'react'
import styled from 'styled-components'
import { useVersioning } from '../../ShareVersionContext'
import { useVersionsHistorySavedScrollPosition } from './VersionsHistoryScrollPositionContext'

const Wrapper = styled.div`
  padding: 8px 8px 8px 0; /* stylelint-disable-line scales/space */
  margin: 0;

  height: 100%;
  overflow-y: auto;
`

type ScrollState = {
  scrollHeight: number
  scrollTop: number
}

interface ShareVersionsListWrapperProps
  extends React.ComponentProps<typeof Wrapper> {
  isInitialDataLoaded: boolean
}

/**
 * A wrapper component for the share versions list that handles scroll restoration for two main use cases:
 * - When the user switches between versions in the version history list,
 *   we want to maintain the scroll position.  @see useInitializeScrollPosition
 * - When the user loads more versions in the reverse infinite scroll, we want to maintain the scroll position
 *   so that the user can continue to see the versions they were looking at.
 *
 * TODO: This code is basically a patch on a patch and the solution does not feel very reliable.
 * Part of the problem is our layout and tree structure. We should probably consider refactoring the layout and the whole
 * version history list to use a more robust solution, maybe even based on a virtualized list library.
 */
const ShareVersionsListWrapper: React.FC<ShareVersionsListWrapperProps> =
  React.forwardRef(function ShareVersionsListWrapper(props, ref) {
    const { children, isInitialDataLoaded, ...listProps } = props
    const scrollContainerRef = useRef<HTMLUListElement | null>(null)
    const childCount = React.Children.count(children)

    useImperativeHandle(ref, () => scrollContainerRef.current)

    const lastScrollStateRef = useTrackLastScrollState(
      isInitialDataLoaded,
      scrollContainerRef
    )

    useInitializeScrollPosition(
      isInitialDataLoaded,
      scrollContainerRef,
      lastScrollStateRef
    )

    useMaintainScrollPositionOnResize(scrollContainerRef, lastScrollStateRef)

    useMaintainScrollPositionOnChildrenChange(
      isInitialDataLoaded,
      scrollContainerRef,
      lastScrollStateRef,
      childCount
    )

    return (
      <Wrapper ref={scrollContainerRef} {...listProps}>
        {children}
      </Wrapper>
    )
  })

// MARK: Scroll restoration

/**
 * This hook solves a problem we have due to our layout: when the user changes the version
 * of the share by clicking on the version in the version history scrollable list,
 * we often remount the whole view including the scroll container and its content.
 * Because we remount everything from scratch, we lose the scroll position, making it difficult to "quickly"
 * switch between versions when the version history list.
 * To fix this we save the scroll position when the component unmounts in a ref kept in memory through a context provider located
 * above the remounted tree of the view (ShareMainRoutes.tsx). So when this component is mounted:
 * - If there is a value in the context but the share identifier does not match, we don't restore
 *  and initialize to the bottom of the container.
 * - If there is a value in the context but we are viewing the latest version, we also don't restore
 *  and initialize to the bottom of the container (so that clicking "See Latest Version" button in the header
 *  brings you to the bottom of the list).
 * - Otherwise if we have a value in the context, we use it to restore the scroll position.
 * - If there is no value in the context, we scroll to the bottom of the container.
 */
function useInitializeScrollPosition(
  isInitialDataLoaded: boolean,
  scrollContainerRef: React.RefObject<HTMLUListElement>,
  lastScrollStateRef: React.MutableRefObject<ScrollState | undefined>
) {
  const { share, isViewingLatestVersion, versionShortId } = useVersioning()
  const persistedScrollPositionRef = useVersionsHistorySavedScrollPosition()

  // Set the initial scroll position once the component is mounted / is ready
  useEffect(() => {
    // Wait until the initial data is loaded before initializing scroll position
    if (!isInitialDataLoaded) return

    const scrollContainer = scrollContainerRef.current

    if (!scrollContainer) return

    const scrollPositionToRestore = persistedScrollPositionRef.current

    // Only restore if the share identifier matches
    const shouldRestoreScrollPosition =
      scrollPositionToRestore &&
      scrollPositionToRestore.shareId === share.identifier &&
      // When viewing the latest version, we don't restore the scroll position
      // mostly because you can switch to the latest version using the "See Latest Version"
      // button in the header.
      !isViewingLatestVersion

    if (shouldRestoreScrollPosition) {
      scrollContainer.scrollTop = scrollPositionToRestore.scrollTop
    } else {
      const newScrollTop =
        scrollContainer.scrollHeight - scrollContainer.offsetHeight
      scrollContainer.scrollTop = newScrollTop
    }

    // Save when on-loaded so we can restore scroll position later once the component re-mounts.
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      const scrollContainer = scrollContainerRef.current
      if (!scrollContainer) return

      persistedScrollPositionRef.current = {
        scrollTop: scrollContainer.scrollTop || 0,
        shareId: share.identifier,
      }
    }
  }, [
    versionShortId,
    lastScrollStateRef,
    scrollContainerRef,
    isInitialDataLoaded,
    persistedScrollPositionRef,
    share.identifier,
    isViewingLatestVersion,
  ])
}

// MARK: Reverse infinite scroll

/**
 * When the scroll container resizes, we want to maintain the scroll position.
 * This is mostly needed when the "Latest starred version" section appears,
 * reducing the height of the scroll container.
 */
function useMaintainScrollPositionOnResize(
  scrollContainerRef: React.RefObject<HTMLUListElement>,
  lastScrollStateRef: React.MutableRefObject<ScrollState | undefined>
) {
  const currentHeightRef = useRef<number | undefined>(undefined)

  useEffect(() => {
    if (!scrollContainerRef.current) return

    currentHeightRef.current = scrollContainerRef.current.offsetHeight

    const resizeObserver = new ResizeObserver(() => {
      const prevHeight = currentHeightRef.current
      const scrollContainer = scrollContainerRef.current

      if (!scrollContainer) return

      const newOffsetHeight = scrollContainer.offsetHeight
      if (prevHeight !== undefined) {
        // Adjust scrollTop based on container height change
        const offsetHeightDiff = newOffsetHeight - prevHeight
        const newScrollTop = scrollContainer.scrollTop - offsetHeightDiff

        scrollContainer.scrollTop = newScrollTop
      }

      currentHeightRef.current = newOffsetHeight
    })

    resizeObserver.observe(scrollContainerRef.current)

    return () => {
      resizeObserver.disconnect()
    }
  }, [scrollContainerRef, lastScrollStateRef])
}

/**
 * When the number of children of the scroll container change, i.e. when we have loaded more versions
 * in our inverse infinite scroll, we need to maintain the scroll position.
 */
function useMaintainScrollPositionOnChildrenChange(
  isEnabled: boolean,
  scrollContainerRef: React.RefObject<HTMLUListElement>,
  lastScrollStateRef: React.MutableRefObject<ScrollState | undefined>,
  childCount: number
) {
  // Maintain scroll position when children change.
  // The idea is to adjust the scrollTop based on the difference in scrollHeight
  // between the last known scroll state and the current scroll state. That difference
  // is basically how much the content has grown because of those new children,
  // and how much we need to compensate in order to keep visible elements in the same positions.
  useEffect(() => {
    if (!scrollContainerRef.current || !isEnabled) return
    const scrollContainer = scrollContainerRef.current

    const lastScrollState = lastScrollStateRef.current
    if (lastScrollState) {
      const newScrollHeight = scrollContainer.scrollHeight
      const scrollHeightDiff = newScrollHeight - lastScrollState.scrollHeight
      const newScrollTop = lastScrollState.scrollTop + scrollHeightDiff

      scrollContainer.scrollTop = newScrollTop
      updateLastScrollState(lastScrollStateRef, scrollContainerRef)
    }
  }, [childCount, isEnabled, lastScrollStateRef, scrollContainerRef])
}

/**
 * Listen to scroll events on the container and save the scroll state
 * in the returned ref object. This allows us to maintain the scroll position
 * in our reverse infinite scroll.
 * Note: This is different from the scroll state stored in the context accessed through
 * useVersionsHistorySavedScrollPosition @see useInitializeScrollPosition.
 */
function useTrackLastScrollState(
  isEnabled: boolean,
  scrollContainerRef: React.RefObject<HTMLUListElement>
) {
  /**
   * Last known scroll state of the container.
   */
  const lastScrollStateRef = useRef<ScrollState | undefined>(undefined)

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current
    if (!scrollContainer || !isEnabled) return

    const handleScroll = () => {
      // For some reasons we sometimes receive a scroll event with the same scrollTop value
      // but with the scrollHeight changed, in which case we don't want to update the last scroll state
      // as it would skew the calculation in useMaintainScrollPositionOnResize hook.
      if (lastScrollStateRef.current?.scrollTop === scrollContainer.scrollTop) {
        return
      }
      updateLastScrollState(lastScrollStateRef, scrollContainerRef)
    }

    scrollContainer.addEventListener('scroll', handleScroll)

    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll)
    }
  }, [scrollContainerRef, lastScrollStateRef, isEnabled])

  return lastScrollStateRef
}

/**
 * Util to update the last scroll state reference from the container element.
 */
function updateLastScrollState(
  lastScrollStateRef: React.MutableRefObject<ScrollState | undefined>,
  scrollContainerRef: React.RefObject<HTMLUListElement>
) {
  if (scrollContainerRef.current) {
    lastScrollStateRef.current = {
      scrollHeight: scrollContainerRef.current.scrollHeight,
      scrollTop: scrollContainerRef.current.scrollTop,
    }
  }
}

export default ShareVersionsListWrapper
